/**
 * Icon Component
 * Wrapper for SVG icons with consistent styling
 */

import React, { forwardRef } from 'react';
import { Box } from './Box';

/**
 * Icon Component
 * Renders an icon with style props support
 * 
 * @example
 * // Using as prop with react-icons
 * import { FiCheck } from 'react-icons/fi';
 * <Icon as={FiCheck} boxSize={6} color="green.500" />
 * 
 * // Using children
 * <Icon boxSize={5} color="brand.500">
 *   <svg>...</svg>
 * </Icon>
 */
export const Icon = forwardRef(({
  as: Element,
  boxSize = '1em',
  w,
  h,
  width,
  height,
  color = 'currentColor',
  focusable = false,
  children,
  ...props
}, ref) => {
  const iconStyles = {
    w: w || width || boxSize,
    h: h || height || boxSize,
    display: 'inline-block',
    lineHeight: '1em',
    flexShrink: 0,
    color,
    verticalAlign: 'middle',
  };

  // If 'as' prop is provided, render that component
  if (Element) {
    return (
      <Box
        ref={ref}
        as={Element}
        focusable={focusable}
        aria-hidden="true"
        {...iconStyles}
        {...props}
      />
    );
  }

  // If children are provided, render them
  if (children) {
    return (
      <Box
        ref={ref}
        as="svg"
        focusable={focusable}
        aria-hidden="true"
        {...iconStyles}
        {...props}
      >
        {children}
      </Box>
    );
  }

  // Fallback to empty box
  return (
    <Box
      ref={ref}
      as="span"
      {...iconStyles}
      {...props}
    />
  );
});

Icon.displayName = 'Icon';

/**
 * CreateIcon helper
 * Creates a custom icon component from SVG paths
 * 
 * @example
 * const CustomIcon = createIcon({
 *   displayName: 'CustomIcon',
 *   viewBox: '0 0 24 24',
 *   path: <path fill="currentColor" d="M12 4L20 20H4L12 4Z" />,
 * });
 */
export const createIcon = ({
  displayName,
  viewBox = '0 0 24 24',
  path,
  d,
  defaultProps = {},
}) => {
  const IconComponent = forwardRef((props, ref) => {
    return (
      <Icon
        ref={ref}
        viewBox={viewBox}
        {...defaultProps}
        {...props}
      >
        {path || (d && <path fill="currentColor" d={d} />)}
      </Icon>
    );
  });

  IconComponent.displayName = displayName;
  return IconComponent;
};

export default Icon;
